import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Button, Grid, Input, Icon, Dropdown, Step, Segment, Table, Message, DropdownItemProps, Checkbox, Loader } from 'semantic-ui-react';
import IVisitSlot from '../Models/IVisitSlot';
import OpeningHoursApi from '../api/OpeningHoursApi';
import IVisitCheckoutSession from '../Models/IVisitCheckoutSession';
import BookingsApi from '../api/BookingsApi';
import IBookedVisitSlot from '../Models/IBookedVisitSlot';
import Globals from '../Globals';
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // import locale

dayjs.locale('fr') ;

const ChangementReservationVisite: React.FC = () => {
  const queryParams = new URLSearchParams(document.location.search);
  let initStep = 1;
  const [step, setStep] = useState<number>(initStep);
  const [email, setEmail] = useState<string>(queryParams.get('e') ? atob(queryParams.get('e') || '') : '');
  const [bookingId, setBookingId] = useState<string>(queryParams.get('b') || '');
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [formatedEventDate, setFormatedEventDate] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [visitDayOptions, setVisitDaysOptions] = useState<DropdownItemProps[]>([]);
  const [visitHourOptions, setVisitHourOptions] = useState<DropdownItemProps[]>([]);
  const [visitSlots, setVisitSlots] = useState<IVisitSlot[]>();
  const [visitsPerSlot, setVisitsPerSlot] = useState<IBookedVisitSlot[]>();
  const [currentBooking, setCurrentBooking] = useState<IVisitCheckoutSession>();
  const [proceedingToUpdate, setProceedingToUpdate] = useState<boolean>(false);
  const [displayEmailError, setDisplayEmailError] = useState<boolean>(false);
  const [validatedChange, setValidatedChange] = useState<boolean>(false);
  const emailIsValid = useMemo<boolean>(() => {
    if (email)
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    else
      return false;
  }, [email]);
  const [curDate] = useState<Date>(new Date());

  const executeScroll = () => {
    const elt = document.getElementById('changement-reservation-visite');
    if (elt) elt.scrollIntoView();
  }
  
  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Votre réservation actuelle";
      case 2:
        return "Nouvelle date de visite";
      case 3:
        return "Modification effectuée";
    }
  }, [step]);

  const checkBooking = () => {
    const bApi = new BookingsApi();
    if (bookingId && email) {
      setErrorMessage(undefined);
      bApi.getBooking(bookingId, email).then((b) => {
        if (b.data) {
          const dtResa = dayjs(b.data.eventDate);
          setFormatedEventDate(`${dtResa.format('dddd DD MMM [à] HH[h]')}`);
          if (b.data.status === "PAID")
            setCurrentBooking(b.data);
          else 
            setErrorMessage(`Votre réservation n'est plus valide.`);
        }
      }).catch(() => {
        setErrorMessage('Aucune réservation trouvée pour ce numéro et cette adresse email.')
      })
    }
  };

  const visitIsFull = useCallback((visit: IVisitSlot, date: string) => {
    let curBooked = 0;
    if (visitsPerSlot) {
      const cb = visitsPerSlot.find(v => v.eventDate === `${date}`);
      if (cb) curBooked = cb.booked;
    }
    return (curBooked > (visit.quota ? visit.quota : Globals.MaxPersonsPerSlot));
  }, [visitsPerSlot]);

  const changeBookingDate = () => {
    const bApi = new BookingsApi();
    if (bookingId && email) {
      setErrorMessage(undefined);
      setProceedingToUpdate(true);
      bApi.updateBooking({ Id: bookingId, Email: email, EventDate: new Date(`${selectedDate} ${selectedTime}:00`) })
        .then((b) => {
          setStep(3);
          setProceedingToUpdate(false);
        })
        .catch((reason) => {
          console.log(reason);
          setErrorMessage('Une erreur est survenue lors de la mise à jour. Veuillez nous contacter directement par téléphone ou email.')
          setProceedingToUpdate(false);
        })
    }
  };

  useEffect(() => {
    const daysOfWeek = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const monthsOfYear = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    if (!visitSlots && curDate) {
      const opHoursApi = new OpeningHoursApi();
      Promise.all([opHoursApi.getBookingOpeningHours(), opHoursApi.getBookedVisits()])
      .then(([res, res2]) => {
        setVisitsPerSlot(res2.data);
        
        let visDays: DropdownItemProps[] = [];
        res.data.forEach(r => {
          const day = new Date(r.date);
          
          const year = day.getFullYear().toString();
          const month = day.getMonth() + 1;
          const paddedMonth = (month < 10) ? `0${month}` : month.toString();
          const curday = day.getDate();
          const paddedDay = (curday < 10) ? `0${curday}` : curday.toString();

          const vDate = dayjs(r.date).hour(r.endHour).toDate();
        
          const newValue = { 
            text:  `${daysOfWeek[day.getDay()]} ${paddedDay} ${monthsOfYear[month-1]} ${year}`,
            value: `${year}/${paddedMonth}/${paddedDay}`,
            key: r.date.substr(0, 10),
            disabled: vDate < curDate,
          } as DropdownItemProps;

          const existingIdx = visDays.findIndex((d) => d.value === newValue.value);
          if (existingIdx < 0) 
            visDays.push(newValue);
          else {
            if (!newValue.disabled) // update if valid
              visDays[existingIdx] = newValue;
          }
        });
        setVisitDaysOptions(visDays);
        setVisitSlots(res.data);
      });
    }
  }, [curDate, visitSlots]);

  useEffect(() => {
    let visHours: DropdownItemProps[] = [];
    if (curDate && selectedDate && visitDayOptions && visitSlots) {
      const selectedDateKey = visitDayOptions.find(v => v.value === selectedDate);
      if (selectedDateKey) {

        const initialDate = visitSlots.filter(v => v.date.indexOf(selectedDateKey.key) === 0);
        if (initialDate && initialDate.length > 0){

          initialDate.forEach((d) => {
            for(let idx = d.startHour; idx < d.endHour; idx++){
              const date = `${d.date.split('T')[0]}T${idx}:00:00`;
              const isFull = visitIsFull(d, date);
              const selectableDateTime = dayjs(date).toDate();

              visHours.push({
                key: `${idx}:00`,
                text: `${idx}h${(isFull ? " (complet)" : "")}${(curDate > selectableDateTime) ? " (fini)" : ""}`,
                value: `${idx}:00`,
                disabled: (isFull || curDate > selectableDateTime),
              });
            }
          });          
        }
      }
    }
    setVisitHourOptions(visHours);
  }, [curDate, selectedDate, visitDayOptions, visitSlots, visitsPerSlot, visitIsFull]);

  return (
    <Container fluid>
      { step <= 3 && (
        <Step.Group widths="three" size="tiny">
          <Step active={step === 1} icon="ticket" title="Réservation actuelle" completed={step > 1} />
          <Step active={step === 2} icon="calendar alternate outline" title="Nouvel horaire" completed={step > 2} />
          <Step active={step === 3} completed={step === 3} icon="calendar check outline" title="Validation" />
        </Step.Group>
      )}
      <h4>{stepTitle}</h4>
      { !visitSlots && (
        <Segment textAlign='center'>
          <Loader active inline size='small' />
          Chargement du planning des réservations...
        </Segment>
      )}
      { visitSlots && step === 1 && (
        <React.Fragment>
          { !currentBooking && (
            <Grid stackable>
              <Grid.Row columns="2">
                <Grid.Column><label>Saisissez votre numéro de réservation *</label>
                  <Input
                    key="numresa"
                    fluid
                    placeholder='Format: 2 lettres suivies de 12 chiffres'
                    onChange={(ev, { value }) => { setBookingId(value); }}
                    value={(bookingId)}
                  />
                </Grid.Column>
                <Grid.Column>
                  <label>Saisissez l'adresse email utilisée pour la réservation *</label>
                  <Input value={email} key="email"
                    fluid
                    onChange={(ev, { value }) => { setEmail(value); }}
                    onBlur={() => {
                      setDisplayEmailError(!emailIsValid);
                    }} />
                  {displayEmailError && (
                    <Message size="mini" negative>Email invalide</Message>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
          { currentBooking && (
            <Table celled striped unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell colSpan='2'>Votre réservation actuelle est valable pour la visite du <b>{formatedEventDate}</b></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {currentBooking.individualNLG > 0 && (
                  <Table.Row>
                    <Table.Cell>Entrée individuelle <b style={{ whiteSpace: 'nowrap'}}>Résident Noisy-Le-Grand *</b></Table.Cell>
                    <Table.Cell collapsing textAlign="right">x {currentBooking.individualNLG}</Table.Cell>
                  </Table.Row>
                )}
                {currentBooking.familyNLG > 0 && (
                  <Table.Row>
                    <Table.Cell>Entrée Famille <b style={{ whiteSpace: 'nowrap'}}>Résident Noisy-Le-Grand *</b></Table.Cell>
                    <Table.Cell collapsing textAlign="right">x {currentBooking.familyNLG}</Table.Cell>
                  </Table.Row>
                )}
                {currentBooking.individualHNLG > 0 && (
                  <Table.Row>
                    <Table.Cell>Entrée individuelle Hors Noisy-Le-Grand</Table.Cell>
                    <Table.Cell collapsing textAlign="right">x {currentBooking.individualHNLG}</Table.Cell>
                  </Table.Row>
                )}
                {currentBooking.familyHNLG > 0 && (
                  <Table.Row>
                    <Table.Cell>Entrée Famille Hors Noisy-Le-Grand</Table.Cell>
                    <Table.Cell collapsing textAlign="right">x {currentBooking.familyHNLG}</Table.Cell>
                  </Table.Row>
                )}
                {currentBooking.free > 0 && (
                  <Table.Row>
                    <Table.Cell>Entrée gratuite enfant moins de 2 ans</Table.Cell>
                    <Table.Cell collapsing textAlign="right">x {currentBooking.free}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
          { currentBooking && currentBooking.show && (
            <Message negative={true}>
              Votre billet a déjà été validé, vous ne pouvez plus procéder à sa modification.
            </Message>
          )}
          <Message size="mini"><b>* Un justificatif de domicile pourra vous être demandé pour le tarif résident Noisy-Le-Grand </b></Message>
          <Grid>
            <Grid.Row columns="2">
              <Grid.Column>
                {!currentBooking && (<Button size='medium' color='green' onClick={checkBooking} icon labelPosition="right"><Icon name="user"></Icon> Vérifier ma réservation</Button>)}
              </Grid.Column>
              <Grid.Column textAlign="right">
                <Button size='medium' color='green' onClick={() => { setStep(2); executeScroll() }} icon disabled={!currentBooking || currentBooking.show} labelPosition="right"><Icon name="user"></Icon> Etape suivante</Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </React.Fragment>)
      }
      { step === 2 && currentBooking && (<>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column><label>Nouvelle date *</label>
              <Dropdown
                placeholder='Sélectionnez le nouveau jour de visite souhaité'
                fluid
                selection
                key="visitDayChoice"
                value={selectedDate}
                onChange={(e, data) => { setSelectedDate(data.value as string); setSelectedTime(''); }}
                options={visitDayOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <label>Nouveau créneau horaire *</label>
              <Dropdown
                placeholder="Sélectionnez la nouvelle heure de visite souhaitée"
                fluid
                selection
                key="visitHourChoice"
                value={selectedTime}
                onChange={(e, data) => { setSelectedTime(data.value as string); }}
                options={visitHourOptions}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Segment>
          <p>Pour rappel, votre réservation actuelle est valable pour la visite du <b>{formatedEventDate}</b>.</p>
          <Checkbox
            label='En cochant cette case, je confirme ma demande de changement de date et heure de visite de ma réservation. '
            onChange={() => setValidatedChange(!validatedChange)}
            checked={validatedChange}
          />
                &nbsp;Cette demande sera envoyée au clic sur le bouton <b>Confirmer</b> ci-dessous
            </Segment>
        {proceedingToUpdate && (
          <Segment textAlign='center'>
            <Loader active inline size='small' />
                Veuillez patienter, votre demande est en cours de traitement...
          </Segment>)
        }
        <Grid>
          <Grid.Row>
            <Grid.Column textAlign="right">
              <Button size='medium' color='green' onClick={changeBookingDate} icon disabled={!currentBooking || !validatedChange || !selectedDate || !selectedTime || proceedingToUpdate} labelPosition="right"><Icon name="user"></Icon> Confirmer</Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </>
      )}
      { step === 3 && (
        <>
          <Segment color="green">
            <p>Votre réservation a été mise à jour, un email de confirmation va vous être envoyé.</p>
            <p>A noter que le numéro de réservation et le QRCode restent les mêmes et pourront donc être utilisés comme preuve de réservation à l'accueil lors de votre venue.</p>
            <p>A très bientôt !</p>
            <p className="noprint"><Button onClick={() => { document.location.assign('/') }} color="green">Retourner à l'accueil du site</Button></p>
          </Segment>
        </>
      )}
      <Message negative={true} hidden={!errorMessage}>{errorMessage}</Message>
    </Container>
  );
}

export default ChangementReservationVisite;
